/**
 * Invoice attribution, v4.
 *
 * Places every line of Adwin's invoice detail (the monthly Commission report
 * rows Adwin billed on) against exactly one Raketech account, or against
 * nothing, and says which rule did it. Nothing is silently dropped and nothing
 * is counted twice: the sum of every line equals the sum attributed plus the
 * sum left unattributed, and that identity is returned with the result so it
 * can be checked.
 *
 * Why this exists. The v3 comparison matched invoice lines only against the
 * accounts in Raketech's June 2025 reconciliation. Accounts that first appear
 * in Raketech's July 2026 report — everything created from December 2024 — were
 * outside that universe, so their lines were never attached to them and the
 * accounts read as invoiced at nil. Paratiisi (18466) is the plain case: two
 * lines a month under RT_RA_PRT_A114_FI_AID10202, one per deal, summing to the
 * cent to what the July report records, and v3 showed nothing against it.
 *
 * Here the universe is the union of every account either side has named — the
 * June file, the Partner Account sheet and the July report, keyed on the id
 * they share — and each account carries every offer code, brand, partner and
 * geo that any of those sources gives it. A line is placed by the strongest
 * rule that identifies it: the deal code first, in successively looser forms,
 * then the brand, then the brand named inside a free-text deal label. Where a
 * rule leaves more than one account standing, the tie is broken on the brand,
 * partner and geo the line itself names, then on whether the account existed
 * in the line's month, and only as a last resort on which account is the
 * larger. Every tie broken that way is flagged, so the report can show where
 * attribution rests on a judgement rather than on the line's own words.
 */

export type RuleId =
  | 'code'            // deal code on the line equals an account's code, as written
  | 'code_token'      // the canonical RT_/LR_ token inside a longer or suffixed label
  | 'code_stem'       // the same, ignoring the trailing _AIDnnnn / _NEW qualifiers
  | 'brand_id_code'   // line had no usable code; another line with the same brand id and geo did
  | 'brand'           // the line's brand (and geo where known) names one account
  | 'brand_in_label'  // a free-text deal label contains one account's brand
  | 'none';

export interface InvoiceLine {
  id: string;
  brand: string;
  brandName?: string | null;
  brandId?: string | null;
  partner: string;
  offer: string;
  geo: string;
  /** As imported, e.g. "December 2024" or "2024-12". */
  month: string;
  ftds: number;
  total: number;
}

export interface AccountProfile {
  id: string;
  /** Every brand name any source gives the account. */
  brands: string[];
  partners: string[];
  /** Every offer / deal code any source gives the account, as written. */
  offers: string[];
  geos: string[];
  /** First month the account can carry invoicing, `YYYY-MM`, where known. */
  created?: string;
  /** Months in which Raketech's own reports record earnings on the account.
   *  When two accounts share a deal code, the one Raketech itself reports for
   *  the line's month takes it. */
  activeMonths?: string[];
  /** The same, per deal code: months Raketech reports the account under that
   *  code. Decides before `activeMonths` where both siblings are active but
   *  only one is reported under the code the line carries. PariMatch is the
   *  case: 12435 and 13826 are both reported for April to June 2023, but only
   *  12435 under RT_PM_PM_A08_BR — and its lines sum to the cent to 12435's
   *  own Reported figure. */
  activeByCode?: Record<string, string[]>;
  /** What Raketech reports on the account by month, across the sources. Where
   *  a line names no deal code and several sibling accounts of the brand are
   *  reported in its month, the line is apportioned in these proportions —
   *  Adwin invoiced on Raketech's report, so the report's own split is the
   *  invoice's split. */
  reported?: Record<string, number>;
  /** For the last-resort tie-break only: earnings across the sources. */
  weight: number;
  sources: ('june' | 'partners' | 'july')[];
}

export interface LineAttribution {
  lineId: string;
  accountId: string | null;
  rule: RuleId;
  /** Accounts the rule identified before tie-breaking, when more than one. */
  candidates?: string[];
  /** Set when the choice between candidates came down to size rather than to
   *  anything the line itself says. */
  byWeight?: boolean;
  /** Set when the line was apportioned across candidates in the proportions
   *  Raketech's own reports give them for the month. The fraction placed here. */
  share?: number;
}

export interface MonthlyInvoiced {
  revenue: number;
  ftds: number;
  lineIds: string[];
}

export interface InvoicingEntry {
  label: string;
  monthly: Map<string, MonthlyInvoiced>;
  /** Rules that placed lines in this entry, strongest first. */
  rules: RuleId[];
  /** Lines in this entry that rest on a weight tie-break. */
  judged: number;
  /** Lines in this entry apportioned in Raketech's own reported proportions. */
  split: number;
  /** Other accounts that were candidates for lines in this entry. */
  contested?: string[];
}

export interface AccountInvoicing {
  accountId: string;
  monthly: Map<string, MonthlyInvoiced>;
  /** Lines grouped by the deal label they were raised under, for drilldown. */
  entries: Map<string, InvoicingEntry>;
  total: number;
  /** How many of this account's lines rest on a weight tie-break. */
  judged: number;
}

export interface AttributionResult {
  byAccount: Map<string, AccountInvoicing>;
  lines: LineAttribution[];
  unattributed: InvoiceLine[];
  /** The identity the whole thing must satisfy. */
  conservation: { all: number; attributed: number; unattributed: number; balanced: boolean };
  ruleCounts: Record<RuleId, { lines: number; revenue: number }>;
  /** Lines apportioned across siblings in Raketech's reported proportions. */
  split: { lines: number; revenue: number };
  /** Second and later copies of lines identical in every field to another line
   *  in the same month. Kept in the attribution — if Adwin invoiced the line
   *  twice, it was invoiced twice, and leaving it in is the conservative
   *  reading — and listed so they can be checked against the invoice PDFs. */
  duplicates: InvoiceLine[];
}

// ---------------------------------------------------------------------------
// Normalisation
// ---------------------------------------------------------------------------

const MONTH_NAMES: Record<string, string> = {
  january: '01', february: '02', march: '03', april: '04', may: '05', june: '06',
  july: '07', august: '08', september: '09', october: '10', november: '11', december: '12',
};

export function monthKey(month: string): string {
  const s = String(month || '').trim();
  if (/^\d{4}-\d{2}$/.test(s)) return s;
  const slash = s.match(/^\d{2}\/(\d{2})\/(\d{4})$/);
  if (slash) return `${slash[2]}-${slash[1]}`;
  const named = s.match(/([A-Za-z]+)\s+(\d{4})/);
  if (named) {
    const mm = MONTH_NAMES[named[1].toLowerCase()];
    if (mm) return `${named[2]}-${mm}`;
  }
  return s;
}

/** Same rules as the comparison endpoint, so the two never disagree on a brand. */
export function normBrand(brand: string | null | undefined): string {
  let n = String(brand || '').toLowerCase()
    .replace(/\s*\([^)]*\)/g, '')
    .replace(/\.(se|com|net|org|bet|casino|io|co|me|tv|us|uk|br|ca|no|fi|de|fr|es|it|jp|pe|cl|mx|dk|at)$/gi, '')
    .replace(/[.\s\-']/g, '');
  n = n.replace(/(casinos?|betting|sports?|bets?|live|games?|group)+$/g, '');
  n = n.replace(/s$/g, '');
  if (n.includes('brazino')) n = 'brazino';
  return n;
}

export function normPartner(partner: string | null | undefined): string {
  const n = String(partner || '').toLowerCase().replace(/\s+/g, '').replace(/[.\-']/g, '');
  if (/^bet(on)?red/.test(n)) return 'betonredpartners';
  return n.replace(/(affiliates?|partners?|affiliation|media|network)+$/g, '');
}

export function normGeo(geo: string | null | undefined): string {
  const g = String(geo || '').replace(/\s*\([^)]*\)/g, '').trim().toUpperCase();
  return g === 'UK' ? 'GB' : g;
}

/** Labels that name no deal at all. Treated as blank. */
const PLACEHOLDER = /^(default( campaign)?|unable to check|link|fb link|wr link|none|pending|n\/?a|-+|tbd|unknown)$/i;

/** The deal code as written, minus parenthetical suffixes and stray spacing. */
export function canonCode(offer: string | null | undefined): string {
  const s = String(offer || '').trim();
  if (!s || PLACEHOLDER.test(s)) return '';
  return s.replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim().toUpperCase();
}

const TOKEN_RE = /\b(?:RT|LR)_[A-Z0-9]+(?:_[A-Z0-9]+)+\b|\b10202-[A-Z]{2}-[A-Z0-9#&\-]*A\d{5,7}C\b/g;
/** The publisher id Raketech's newer labels end in, e.g. A193242C in
 *  "10202-FI-CAS---#1-Novajackpot-A193242C". An invoice line may carry it in
 *  a parenthesis after an unrelated code: "RT_BA_NJ_A15_FI ( A193242C)". */
const PUBLISHER_ID_RE = /\bA\d{5,7}C\b/g;

/** Canonical deal tokens inside a label, e.g. the RT_… code buried in
 *  "667231 - Bankonbet.com - 2089580 - RT_7S_BK_A22_NO_AID10202 - Media ID Migrated",
 *  and the publisher id where the label carries one. Parentheticals are
 *  searched for the id, because that is where the lines put it. */
export function codeTokens(offer: string | null | undefined): string[] {
  const s = canonCode(offer);
  const rawUpper = String(offer || '').toUpperCase();
  if (!s && !rawUpper) return [];
  const found = [
    ...(s.match(TOKEN_RE) || []),
    ...(rawUpper.match(PUBLISHER_ID_RE) || []),
  ];
  return [...new Set(found.map((t) => t.toUpperCase()))];
}

/** A token with its trailing qualifiers removed, so RT_IA_LMO_A31_FI_NEW_AID10202
 *  and RT_IA_LMO_A31_FI_AID10202 meet. */
export function codeStem(token: string): string {
  return token.replace(/(_AID\d+|_NEW|_OLD|_V\d+)+$/i, '');
}

/** Geo the code itself names, e.g. FI in RT_RA_PRT_A114_FI_AID10202. */
export function geoFromCode(offer: string | null | undefined): string {
  for (const t of codeTokens(offer)) {
    const m = codeStem(t).match(/_([A-Z]{2})(?:\d)?$/);
    if (m) return normGeo(m[1]);
  }
  return '';
}

// ---------------------------------------------------------------------------
// Indexing
// ---------------------------------------------------------------------------

interface Indexed {
  profile: AccountProfile;
  brands: Set<string>;
  partners: Set<string>;
  geos: Set<string>;
  codes: Set<string>;
  tokens: Set<string>;
  stems: Set<string>;
  active: Set<string>;
  /** canonical code or token -> months reported under it */
  activeByCode: Map<string, Set<string>>;
  reported: Map<string, number>;
}

function index(profiles: AccountProfile[]): Indexed[] {
  return profiles.map((p) => {
    const codes = new Set<string>();
    const tokens = new Set<string>();
    const stems = new Set<string>();
    const geos = new Set<string>(p.geos.map(normGeo).filter(Boolean));
    for (const o of p.offers) {
      const c = canonCode(o);
      if (!c) continue;
      codes.add(c);
      for (const t of codeTokens(o)) {
        tokens.add(t);
        stems.add(codeStem(t));
      }
      const g = geoFromCode(o);
      if (g) geos.add(g);
    }
    const brands = new Set<string>();
    for (const b of p.brands) {
      for (const part of String(b || '').split('/')) {
        const nb = normBrand(part);
        if (nb) brands.add(nb);
      }
    }
    const activeByCode = new Map<string, Set<string>>();
    for (const [code, months] of Object.entries(p.activeByCode || {})) {
      const keys = [canonCode(code), ...codeTokens(code)].filter(Boolean);
      for (const k of keys) {
        const set = activeByCode.get(k) || new Set<string>();
        for (const m of months) set.add(m);
        activeByCode.set(k, set);
      }
    }
    return {
      profile: p,
      brands,
      partners: new Set(p.partners.map(normPartner).filter(Boolean)),
      geos,
      codes,
      tokens,
      stems,
      active: new Set(p.activeMonths || []),
      activeByCode,
      reported: new Map(Object.entries(p.reported || {}).map(([m, v]) => [m, Math.abs(Number(v) || 0)])),
    };
  });
}

// ---------------------------------------------------------------------------
// Attribution
// ---------------------------------------------------------------------------

function lineBrands(line: InvoiceLine): Set<string> {
  const out = new Set<string>();
  for (const b of [line.brand, line.brandName]) {
    const nb = normBrand(b);
    if (nb) out.add(nb);
  }
  return out;
}

const intersects = <T,>(a: Set<T>, b: Set<T>): boolean => {
  for (const x of a) if (b.has(x)) return true;
  return false;
};

/**
 * Narrows a set of candidates on what the line itself says, in order of how
 * much it says: brand, then partner, then geo, then what Raketech reports for
 * the month. Where that still leaves several, and Raketech reports an amount
 * on more than one of them in the month, the line is apportioned in those
 * proportions; only where it reports on none is the largest account taken.
 */
function refine(
  cands: Indexed[],
  line: InvoiceLine,
  month: string,
  /** What has already been placed on each account in this month, so a sibling
   *  whose reported figure its own coded lines already cover takes no share. */
  placedSoFar: (id: string, month: string) => number,
): { survivors: Indexed[]; byWeight: boolean; shares?: Map<Indexed, number> } {
  let pool = cands;
  const brands = lineBrands(line);
  const partner = normPartner(line.partner);
  const geo = normGeo(line.geo) || geoFromCode(line.offer);
  const codeKeys = [canonCode(line.offer), ...codeTokens(line.offer)].filter(Boolean);

  const narrow = (keep: (c: Indexed) => boolean) => {
    if (pool.length <= 1) return;
    const kept = pool.filter(keep);
    if (kept.length > 0) pool = kept;
  };

  // An account whose sources name no partner or no geo is not excluded by a
  // partner or geo test: nothing is known to contradict the line.
  narrow((c) => brands.size > 0 && intersects(brands, c.brands));
  narrow((c) => !!partner && (c.partners.size === 0 || c.partners.has(partner)));
  narrow((c) => !!geo && (c.geos.size === 0 || c.geos.has(geo)));
  // Raketech's own reports name the account that earned under this code in
  // this month, and failing that the account that earned in this month at
  // all. Where one candidate is reported and the others are not, that is the
  // line's home; where none or all are, it decides nothing.
  narrow((c) => codeKeys.some((k) => c.activeByCode.get(k)?.has(month)));
  narrow((c) => c.active.has(month));
  narrow((c) => !c.profile.created || c.profile.created <= month);

  if (pool.length <= 1) return { survivors: pool, byWeight: false };

  // Adwin invoiced on Raketech's report. Where the report carries the brand
  // on several of these accounts in the month, its split is the invoice's —
  // measured on what each account's reported figure still has uncovered
  // after the lines that named it by code. Adwin invoices net of Raketech's
  // 5%, so what is placed is grossed up before it is set against the report.
  const uncovered = new Map<Indexed, number>();
  for (const c of pool) {
    const rep = c.reported.get(month) || 0;
    if (rep <= 0) continue;
    const left = rep - placedSoFar(c.profile.id, month) / 0.95;
    if (left > 0.5) uncovered.set(c, left);
  }
  if (uncovered.size === 1) return { survivors: [[...uncovered.keys()][0]], byWeight: false };
  if (uncovered.size > 1) {
    const total = [...uncovered.values()].reduce((s, v) => s + v, 0);
    const shares = new Map<Indexed, number>();
    for (const [c, v] of uncovered) shares.set(c, v / total);
    return { survivors: [...uncovered.keys()], byWeight: false, shares };
  }

  const max = Math.max(...pool.map((c) => c.profile.weight));
  const top = pool.filter((c) => c.profile.weight === max);
  return { survivors: top.length === 1 ? top : [top[0]], byWeight: true };
}

export function attributeInvoices(
  lines: InvoiceLine[],
  profiles: AccountProfile[],
): AttributionResult {
  const accounts = index(profiles);

  const byCode = new Map<string, Indexed[]>();
  const byToken = new Map<string, Indexed[]>();
  const byStem = new Map<string, Indexed[]>();
  const byBrand = new Map<string, Indexed[]>();
  const push = (m: Map<string, Indexed[]>, k: string, a: Indexed) => {
    if (!m.has(k)) m.set(k, []);
    m.get(k)!.push(a);
  };
  for (const a of accounts) {
    for (const c of a.codes) push(byCode, c, a);
    for (const t of a.tokens) push(byToken, t, a);
    for (const s of a.stems) push(byStem, s, a);
    for (const b of a.brands) push(byBrand, b, a);
  }

  // A brand id with a geo resolves to a deal code where any line carrying that
  // pair also carries a real code. Covers the months whose Commission report
  // had no Offer column.
  const codeByBrandIdGeo = new Map<string, string>();
  for (const l of lines) {
    if (!l.brandId) continue;
    const c = canonCode(l.offer);
    if (!c) continue;
    const k = `${l.brandId}|${normGeo(l.geo)}`;
    if (!codeByBrandIdGeo.has(k)) codeByBrandIdGeo.set(k, c);
  }

  const result: LineAttribution[] = [];
  const unattributed: InvoiceLine[] = [];
  const byAccount = new Map<string, AccountInvoicing>();
  const ruleCounts = {} as Record<RuleId, { lines: number; revenue: number }>;
  const rules: RuleId[] = ['code', 'code_token', 'code_stem', 'brand_id_code', 'brand', 'brand_in_label', 'none'];
  for (const r of rules) ruleCounts[r] = { lines: 0, revenue: 0 };

  let splitLines = 0;
  let splitRevenue = 0;

  const place = (
    line: InvoiceLine,
    month: string,
    acc: Indexed,
    rule: RuleId,
    cands: Indexed[],
    byWeight: boolean,
    share = 1,
  ) => {
    const id = acc.profile.id;
    const revenue = (line.total || 0) * share;
    const ftds = (line.ftds || 0) * share;
    let inv = byAccount.get(id);
    if (!inv) {
      inv = { accountId: id, monthly: new Map(), entries: new Map(), total: 0, judged: 0 };
      byAccount.set(id, inv);
    }
    const m = inv.monthly.get(month) || { revenue: 0, ftds: 0, lineIds: [] };
    m.revenue += revenue;
    m.ftds += ftds;
    m.lineIds.push(line.id);
    inv.monthly.set(month, m);

    const label = canonCode(line.offer)
      ? `${canonCode(line.offer)} — ${line.brandName || line.brand}`
      : (line.brandName || line.brand || 'Invoice');
    const e: InvoicingEntry = inv.entries.get(label) || { label, monthly: new Map(), rules: [], judged: 0, split: 0 };
    const em = e.monthly.get(month) || { revenue: 0, ftds: 0, lineIds: [] };
    em.revenue += revenue;
    em.ftds += ftds;
    em.lineIds.push(line.id);
    e.monthly.set(month, em);
    if (!e.rules.includes(rule)) e.rules.push(rule);
    if (byWeight) e.judged += 1;
    if (share < 1) e.split += 1;
    if (cands.length > 1) {
      const others = cands.map((c) => c.profile.id).filter((x) => x !== id);
      e.contested = [...new Set([...(e.contested || []), ...others])];
    }
    inv.entries.set(label, e);

    inv.total += revenue;
    if (byWeight) inv.judged += 1;

    result.push({
      lineId: line.id,
      accountId: id,
      rule,
      candidates: cands.length > 1 ? cands.map((c) => c.profile.id) : undefined,
      byWeight: byWeight || undefined,
      share: share < 1 ? share : undefined,
    });
    ruleCounts[rule].lines += share;
    ruleCounts[rule].revenue += revenue;
  };

  const placedSoFar = (id: string, month: string): number =>
    byAccount.get(id)?.monthly.get(month)?.revenue || 0;

  // Exact repeats: every field the same, in the same month. Not removed. The
  // brand is compared as written, because a suffix such as "(253)" marks a
  // second deal line on the same brand, which is not a repeat.
  const duplicates: InvoiceLine[] = [];
  {
    const seen = new Set<string>();
    for (const l of lines) {
      if (Math.abs(l.total || 0) < 0.005) continue;
      const key = [
        monthKey(l.month), canonCode(l.offer),
        String(l.brand || '').trim().toLowerCase(), String(l.brandName || '').trim().toLowerCase(),
        normPartner(l.partner), normGeo(l.geo), l.ftds || 0, (l.total || 0).toFixed(2),
      ].join('|');
      if (seen.has(key)) duplicates.push(l); else seen.add(key);
    }
  }

  // Lines that name a deal code go first, so that when a line naming only a
  // brand is apportioned, the siblings' coded lines are already in place and
  // the split falls on what their reported figures still leave uncovered.
  const namesCode = (l: InvoiceLine) => {
    const c = canonCode(l.offer);
    if (!c) return false;
    return byCode.has(c) || codeTokens(l.offer).some((t) => byToken.has(t) || byStem.has(codeStem(t)));
  };
  const ordered = [...lines.filter(namesCode), ...lines.filter((l) => !namesCode(l))];

  for (const line of ordered) {
    const month = monthKey(line.month);
    const code = canonCode(line.offer);
    const tokens = codeTokens(line.offer);
    const stems = tokens.map(codeStem);

    let cands: Indexed[] = [];
    let rule: RuleId = 'none';

    const tryRule = (r: RuleId, found: Indexed[] | undefined) => {
      if (rule !== 'none' || !found || found.length === 0) return;
      cands = [...new Set(found)];
      rule = r;
    };

    if (code) {
      tryRule('code', byCode.get(code));
      tryRule('code_token', tokens.flatMap((t) => byToken.get(t) || []));
      tryRule('code_stem', stems.flatMap((s) => byStem.get(s) || []));
    }
    if (rule === 'none' && !code && line.brandId) {
      const resolved = codeByBrandIdGeo.get(`${line.brandId}|${normGeo(line.geo)}`);
      if (resolved) {
        const t = codeTokens(resolved);
        tryRule('brand_id_code', [
          ...(byCode.get(resolved) || []),
          ...t.flatMap((x) => byToken.get(x) || []),
        ]);
      }
    }
    if (rule === 'none') {
      const brands = lineBrands(line);
      tryRule('brand', [...brands].flatMap((b) => byBrand.get(b) || []));
    }
    if (rule === 'none' && code) {
      // Free-text labels such as "FROSTYBET PNP FI" or "LEGIANO FI": the brand
      // is in the words, so look for an account brand inside them.
      const words = normBrand(code.replace(/[^A-Z0-9 ]/g, ' '));
      const hits = accounts.filter((a) => [...a.brands].some((b) => b.length >= 4 && words.includes(b)));
      tryRule('brand_in_label', hits);
    }

    if (rule === 'none') {
      unattributed.push(line);
      result.push({ lineId: line.id, accountId: null, rule: 'none' });
      ruleCounts.none.lines += 1;
      ruleCounts.none.revenue += line.total || 0;
      continue;
    }

    const { survivors, byWeight, shares } = refine(cands, line, month, placedSoFar);
    if (shares) {
      splitLines += 1;
      splitRevenue += line.total || 0;
      for (const acc of survivors) place(line, month, acc, rule, cands, false, shares.get(acc) || 0);
    } else {
      place(line, month, survivors[0], rule, cands, byWeight);
    }
  }

  const r2 = (n: number) => Math.round(n * 100) / 100;
  const all = r2(lines.reduce((s, l) => s + (l.total || 0), 0));
  const attributed = r2([...byAccount.values()].reduce((s, a) => s + a.total, 0));
  const un = r2(unattributed.reduce((s, l) => s + (l.total || 0), 0));
  for (const a of byAccount.values()) {
    a.total = r2(a.total);
    for (const m of a.monthly.values()) m.revenue = r2(m.revenue);
    for (const e of a.entries.values()) for (const m of e.monthly.values()) m.revenue = r2(m.revenue);
  }
  for (const r of rules) {
    ruleCounts[r].revenue = r2(ruleCounts[r].revenue);
    ruleCounts[r].lines = Math.round(ruleCounts[r].lines);
  }

  return {
    byAccount,
    lines: result,
    unattributed,
    conservation: { all, attributed, unattributed: un, balanced: Math.abs(all - attributed - un) < 0.01 },
    ruleCounts,
    split: { lines: splitLines, revenue: r2(splitRevenue) },
    duplicates,
  };
}

// ---------------------------------------------------------------------------
// Wire format
// ---------------------------------------------------------------------------

export interface MonthlyWire { revenue: number; ftds: number }

export interface EntryWire {
  label: string;
  rules: RuleId[];
  judged: number;
  split?: number;
  contested?: string[];
  monthly: Record<string, MonthlyWire>;
}

export interface AccountInvoicingWire {
  total: number;
  judged: number;
  monthly: Record<string, MonthlyWire>;
  entries: EntryWire[];
}

export interface LineWire {
  id: string;
  month: string;
  offer: string;
  brand: string;
  partner: string;
  geo: string;
  total: number;
  accountId?: string | null;
  candidates?: string[];
}

export interface AttributionResponse {
  generatedAt: string;
  lineCount: number;
  accounts: Record<string, AccountInvoicingWire>;
  unattributed: LineWire[];
  /** Lines whose account was decided on size, for review. */
  judged: LineWire[];
  conservation: AttributionResult['conservation'];
  ruleCounts: AttributionResult['ruleCounts'];
  split: AttributionResult['split'];
  /** Exact repeats of another line in the same month, kept in and listed. */
  duplicates: LineWire[];
}

const wireLine = (l: InvoiceLine): LineWire => ({
  id: l.id, month: monthKey(l.month), offer: l.offer, brand: l.brandName || l.brand,
  partner: l.partner, geo: l.geo, total: Math.round((l.total || 0) * 100) / 100,
});

export function serializeAttribution(res: AttributionResult, lines: InvoiceLine[]): AttributionResponse {
  const byId = new Map(lines.map((l) => [l.id, l]));
  const monthlyWire = (m: Map<string, MonthlyInvoiced>): Record<string, MonthlyWire> => {
    const out: Record<string, MonthlyWire> = {};
    for (const [k, v] of [...m.entries()].sort(([a], [b]) => a.localeCompare(b))) {
      out[k] = { revenue: Math.round(v.revenue * 100) / 100, ftds: v.ftds };
    }
    return out;
  };
  const accounts: Record<string, AccountInvoicingWire> = {};
  for (const [id, a] of res.byAccount) {
    accounts[id] = {
      total: a.total,
      judged: a.judged,
      monthly: monthlyWire(a.monthly),
      entries: [...a.entries.values()]
        .map((e) => ({ label: e.label, rules: e.rules, judged: e.judged, split: e.split || undefined, contested: e.contested, monthly: monthlyWire(e.monthly) }))
        .sort((x, y) => x.label.localeCompare(y.label)),
    };
  }
  const judged: LineWire[] = [];
  for (const a of res.lines) {
    if (!a.byWeight) continue;
    const l = byId.get(a.lineId);
    if (!l || Math.abs(l.total || 0) < 0.005) continue;
    judged.push({ ...wireLine(l), accountId: a.accountId, candidates: a.candidates });
  }
  return {
    generatedAt: new Date().toISOString(),
    lineCount: lines.length,
    accounts,
    unattributed: res.unattributed.map(wireLine).filter((l) => Math.abs(l.total) >= 0.005),
    judged,
    conservation: res.conservation,
    ruleCounts: res.ruleCounts,
    split: res.split,
    duplicates: res.duplicates.map(wireLine),
  };
}

// ---------------------------------------------------------------------------
// Building the account universe
// ---------------------------------------------------------------------------

export interface JuneReconRow {
  account_id: string | number;
  username?: string;
  partner?: string;
  brand?: string;
  brand_name?: string;
  offer?: string;
  geo?: string;
  month?: string;
  earn?: number;
  total_new_earnings?: number;
  is_correction?: number;
}

export interface PartnerSheetRow {
  account_id: string | number;
  partner?: string;
  brand?: string;
  brand_name?: string;
  offer?: string;
  geo?: string;
  our_comm?: number;
  our_total_commission?: number;
}

export interface JulyOffer {
  hubId: string | number;
  partner?: string;
  brand?: string;
  offer?: string;
  created?: string | null;
  monthly?: Record<string, number>;
  adwinEarnings?: number;
  totalEarnings?: number;
}

/**
 * One profile per account id across the three sources. The id is the one both
 * of Raketech's reports use — the June file's ID is the July report's HUB ID,
 * which Raketech confirmed on 9 August 2026 and Adwin accepted.
 */
export function buildAccountProfiles(
  june: JuneReconRow[],
  partners: PartnerSheetRow[],
  july: JulyOffer[],
): AccountProfile[] {
  const map = new Map<string, AccountProfile>();
  const juneReported = new Map<string, Record<string, number>>();
  const get = (id: string) => {
    let p = map.get(id);
    if (!p) {
      p = { id, brands: [], partners: [], offers: [], geos: [], weight: 0, sources: [] };
      map.set(id, p);
    }
    return p;
  };
  const add = (arr: string[], v: string | undefined | null) => {
    const s = String(v || '').trim();
    if (s && !arr.includes(s)) arr.push(s);
  };
  const active = (
    p: AccountProfile,
    month: string | undefined,
    amount: number | undefined,
    code?: string | null,
  ) => {
    if (!month || !amount) return;
    const m = monthKey(month);
    if (!p.activeMonths) p.activeMonths = [];
    if (!p.activeMonths.includes(m)) p.activeMonths.push(m);
    const c = String(code || '').trim();
    if (!c) return;
    if (!p.activeByCode) p.activeByCode = {};
    if (!p.activeByCode[c]) p.activeByCode[c] = [];
    if (!p.activeByCode[c].includes(m)) p.activeByCode[c].push(m);
  };

  for (const r of june) {
    if (r.is_correction) continue;
    const id = String(r.account_id || '').trim();
    if (!id) continue;
    const p = get(id);
    add(p.brands, r.brand_name); add(p.brands, r.brand);
    add(p.partners, r.partner);
    add(p.offers, r.offer);
    add(p.geos, r.geo);
    const earn = Number(r.earn ?? r.total_new_earnings) || 0;
    p.weight += Math.abs(earn);
    active(p, r.month, earn, r.offer);
    if (earn && r.month) {
      const m = monthKey(r.month);
      const j = juneReported.get(id) || {};
      j[m] = (j[m] || 0) + Math.abs(earn);
      juneReported.set(id, j);
    }
    if (!p.sources.includes('june')) p.sources.push('june');
  }
  for (const r of partners) {
    const id = String(r.account_id || '').trim();
    if (!id) continue;
    const p = get(id);
    add(p.brands, r.brand_name); add(p.brands, r.brand);
    add(p.partners, r.partner);
    add(p.offers, r.offer);
    add(p.geos, r.geo);
    p.weight += Math.abs(Number(r.our_comm ?? r.our_total_commission) || 0);
    if (!p.sources.includes('partners')) p.sources.push('partners');
  }
  for (const o of july) {
    const id = String(o.hubId || '').trim();
    if (!id) continue;
    const p = get(id);
    add(p.brands, o.brand);
    add(p.partners, o.partner);
    add(p.offers, o.offer);
    p.weight += Math.abs(Number(o.adwinEarnings ?? o.totalEarnings) || 0);
    for (const [m, v] of Object.entries(o.monthly || {})) {
      const amount = Number(v) || 0;
      active(p, m, amount, o.offer);
      if (amount) {
        if (!p.reported) p.reported = {};
        p.reported[monthKey(m)] = (p.reported[monthKey(m)] || 0) + Math.abs(amount);
      }
    }
    if (o.created && !p.created) p.created = String(o.created).slice(0, 7);
    if (!p.sources.includes('july')) p.sources.push('july');
  }
  // An account first seen in the June file existed through the reconciled
  // period, whatever the July report says about when it was created. Its
  // reported amounts come from the July report where it has one — the two
  // files describe the same months — and from the June rows otherwise.
  for (const p of map.values()) {
    if (p.sources.includes('june') || p.sources.includes('partners')) p.created = undefined;
    if (!p.reported && juneReported.has(p.id)) p.reported = juneReported.get(p.id);
  }
  return [...map.values()];
}
